<template lang="pug">
 
  #stage
    canvas(ref="canvas" aria-hidden="true")

</template>

<script>


  // IMPORTS -------------------------------------------------------------------

  import { mapState, mapMutations } from 'vuex'
  import { PATHS, HOME, EVENTS, LOAD, MODAL_STATES } from 'src/config/app'
  import Env from 'src/config/env'
  const THREE = window.THREE = require('three')
  import ResizeService from 'src/services/ResizeService'
  import ImageService from '@/services/ImageService'

  import { clamp, map, DEG_TO_RAD } from '@/helpers/MathUtils'
  import gsap from 'gsap'
  import Pointer from '@/helpers/Pointer'
  import Raf from '@/helpers/Raf'
  import Communicator from '@/helpers/Communicator'
  import AnimationConfig from 'src/config/animation'

  //import HomeBgGradientGroup from '@/components/stage/scene/home/bg-gradient'
  import HomeScene from '@/components/stage/scene/home'
  import GridGroup from '@/components/stage/scene/home/dot-grid'
  import ContentPanelGroup from '@/components/stage/scene/content/panel-bg'
  import MenuPanelGroup from '@/components/stage/scene/menu/panel-bg'
  import dat from 'dat.gui'

  import frag from '@/components/stage/shaders/output.frag'
  import vert from '@/components/stage/shaders/output.vert'


  // CONSTS -------------------------------------------------------------------

  const RENDERER = {
    ANTI_ALIAS: true,
    ALPHA: false, 
    SORT_OBJECTS: false
  }

 

  export default {

    name: PATHS.CLONE,

    components: {},

    data() {

      return { 
      }
    },

    computed: {
      ...mapState('app', [
        'loading',
        'home',
        'reduced_motion',
        'modal_state',
        'motion_clamp'
      ])
    },

    mounted() {
      this.setup()
    },

    beforeDestroy() {},

    methods: {

      ...mapMutations('app', [
        'setScrollLock'
      ]),

      setup(){

        this.prevTime = 0
        this.rendering = true
        
        this.views = []
        this.eventAdded = false

        this.timeScale = 0
        this.DEBUG =  false, //( Env.desktop && Env.env === 'local' )
        this.prevMorphIndex = -1
        this.gui = null

        this.renderer = null
        this.scene = null
        this.camera = null
        this.container = null

        this.options = {

          background: {
            color: 0x464bbf
          },

          content: {
            overlayAmt: 1
          },

          static: {
            amt: 0
          }
        }

        this.welcomeLightTint = 1
        this.welcomeDarkTint = Env.mobile ? 0 : 0.2

        this.tweenObj = {
          startColor: '#191b44',
          stopColor: '#4046b0',
          lightTintAmt: this.welcomeLightTint,
          darkTintAmt: this.welcomeDarkTint
        }

        this.setupThree()
        // this.addGUI()
      },

      setupThree(){

        if(Env.mobile){
          this.options.static.amt = 0.13
        } else if(Env.tablet){
          this.options.static.amt = 0.05
        } else if(Env.desktop){
          this.options.static.amt = 0.09
        }

        this.scene = new THREE.Scene()
        this.camera = new THREE.OrthographicCamera( window.innerWidth / - 2, window.innerWidth / 2, window.innerHeight / 2, window.innerHeight / - 2, 0, 1000 )
        this.camera.position.z = 2
        this.scene.add( this.camera )

        this.renderer = new THREE.WebGLRenderer({ antialias: RENDERER.ANTI_ALIAS, alpha: RENDERER.ALPHA, canvas: this.$refs.canvas })
 
        this.renderer.setPixelRatio( Env.pixelRatio ) 
        this.renderer.setSize( window.innerWidth, window.innerHeight ) 
        this.renderer.sortObjects = RENDERER.SORT_OBJECTS
      },

      setupViews(){

        this.contentPanelBg = new ContentPanelGroup({
         //gui: this.gui,
          reduced_motion: this.reduced_motion
        })
        this.views.push(this.contentPanelBg)
        this.scene.add(this.contentPanelBg)


        this.menuPanelBg = new MenuPanelGroup({
          //gui: this.gui,
          reduced_motion: this.reduced_motion
        })
        this.views.push(this.menuPanelBg)
        this.scene.add(this.menuPanelBg)


        this.homeScene = new HomeScene({
          renderer: this.renderer,
          //gui: this.gui,
          textures: this.textures,
          reduced_motion: this.reduced_motion
        })
        this.views.push(this.homeScene)

        let material_options = {

          uniforms: {

            tHome: {
              value: this.homeScene.rTexture.texture
            },

            tBubble: {
              value:this.homeScene.bubbleFbo.bufferTexture2.texture //this.homeScene.bubbleFbo.bufferTexture2.texture
            },

            tLightTint: {
              value: this.textures.tint_light
            },

            tDarkTint: {
              value: this.textures.tint_dark
            },
 
            lightTintAmt: { 
              value: this.tweenObj.lightTintAmt
            },

            darkTintAmt: { 
              value: this.tweenObj.darkTintAmt
            },

            staticAmt: {
              value: this.options.static.amt
            },

            startColor: {
              value: new THREE.Color( this.tweenObj.startColor )
            },

            stopColor: {
              value: new THREE.Color( this.tweenObj.stopColor )
            },

            overlayAmt: {
              value: this.options.content.overlayAmt
            },

            time: {
              value: 0
            }

          },
          transparent: true,
          vertexShader: vert,
          fragmentShader: frag
        }

        this.material = new THREE.ShaderMaterial(material_options)
        this.plane = new THREE.Mesh( new THREE.PlaneGeometry(1, 1 , 32 ), this.material)
        this.plane.position.z = -3
        this.scene.add(this.plane)


        // this.plane2 = new THREE.Mesh( new THREE.PlaneGeometry(window.innerWidth, window.innerHeight , 32 ), new THREE.MeshBasicMaterial({
        //   map : this.homeScene.bubbleFbo.bufferTexture2.texture
        // }))
        // this.plane2.position.z = -0.5
        // this.scene.add(this.plane2)


        //if(Env.desktop){
        this.grid = new GridGroup({
          // gui : this.gui,
          tBubble: this.homeScene.bubbleFbo.bufferTexture2.texture,
          reduced_motion: this.reduced_motion
        })
        this.views.push(this.grid)
        this.scene.add(this.grid)
        //}


 
        // this.tint_light_plane = new THREE.Mesh( new THREE.PlaneGeometry(window.innerWidth, window.innerHeight , 4 ), new THREE.MeshBasicMaterial({ 
        //   map : this.textures.tint_light,
        //   transparent: true,
        //   opacity: 0.1 
        // }))
        // this.tint_light_plane.position.z = 0
        // this.scene.add(this.tint_light_plane)

        // this.tint_dark_plane = new THREE.Mesh( new THREE.PlaneGeometry(window.innerWidth, window.innerHeight , 4 ), new THREE.MeshBasicMaterial({ 
        //   map : this.textures.tint_light,
        //   transparent: true,
        //   opacity: 0.5,
        //   blending: THREE.AdditiveBlending
        // }))
        // this.tint_dark_plane.position.z = 0
        // this.scene.add(this.tint_dark_plane)

        this.resize()

      },

      createTextures(){

        this.textures = {}
        
        this.textures['blue_matcap'] = new THREE.Texture( ImageService.get( require('@/assets/imgs/blue_matcap.jpg') ) )
        this.textures['blue_matcap'].needsUpdate = true

        this.textures['tint_light'] = new THREE.Texture( ImageService.get( require('@/assets/imgs/home-tint-light.png') ) )
        this.textures['tint_light'].needsUpdate = true

        this.textures['tint_dark'] = new THREE.Texture( ImageService.get( require('@/assets/imgs/home-tint-dark.png') ) )
        this.textures['tint_dark'].needsUpdate = true
      },

      addEvents(){

        this.addRenderEvent()

        ResizeService.on(ResizeService.EVENTS.RESIZE, this.resize)
        Communicator.on(EVENTS.SCROLL_LOCK, this.onScrollLock)
        Communicator.on(EVENTS.STOP_RENDER, this.onStopRender)
        Communicator.on(EVENTS.START_RENDER, this.onStartRender)
        Pointer.on(Pointer.Events.MOVE, this.onPointerMove)
        Pointer.on(Pointer.Events.START, this.onPointerStart)
        Pointer.on(Pointer.Events.END, this.onPointerEnd)
      },

      addRenderEvent(){
        if(!this.reduced_motion && !this.eventAdded){
          Raf.add(this.onTick)
          this.eventAdded = true
        }
      },

      removeRenderEvent(){
        Raf.remove(this.onTick)
        this.eventAdded = false
      },

      // addGUI () {

      //   this.gui = new dat.GUI({
      //     autoPlace: false,
      //     width: 260,
      //     hideable: true
      //   })

      //   let bgFolder = this.gui.addFolder('Bg');
      //   bgFolder.addColor(this.options.background, 'color').onChange(()=>{
      //     this.renderer.setClearColor(this.state.bgColor)
      //   })

      //   let staticFolder = this.gui.addFolder('Static');
      //   staticFolder.add(this.options.static, 'amt', 0, 1).step(0.01).onChange(()=>{
      //     this.material.uniforms.staticAmt.value = this.options.static.amt
      //   })

      //   const guiWrap = document.createElement('div');
      //   this.$el.appendChild( guiWrap );
      //   guiWrap.classList.add('gui-wrap');

      //   if(this.DEBUG){
      //     guiWrap.appendChild(this.gui.domElement)
      //     this.gui.close()
      //   }

      // },

      showOverlay(){
        if(Env.mobile){ 
          gsap.to(this.options.content, this.getDuration(2), { overlayAmt: 0.3, onUpdate: () => {
            this.material.uniforms.overlayAmt.value = this.options.content.overlayAmt
          }})
        } 
      },

      hideOverlay(){
        if(Env.mobile){ 
          gsap.to(this.options.content, this.getDuration(2), { overlayAmt: 1, onUpdate: () => {
            this.material.uniforms.overlayAmt.value = this.options.content.overlayAmt
          } })
        }
      },

      getDuration(duration){ 
        let clamp = this.reduced_motion ? 0 : 1
        return ( duration * clamp )
      },

      updateTint(darkTintAmt, lightTintAmt){
        gsap.to(this.tweenObj, this.getDuration(AnimationConfig.home.section.duration), {
          darkTintAmt: darkTintAmt,
          lightTintAmt: lightTintAmt,
          onUpdate: this.onTintUpdate,
          ease: 'Linear.easeNone'
        })
      },

      updateBgColor(colors = this.home.current_section.bgColors){ 
        if(colors){ 
          gsap.to(this.tweenObj, this.getDuration(AnimationConfig.home.section.duration), {
            startColor: colors.startColor,
            stopColor: colors.stopColor,
            onUpdate: this.onBgColorUpdate,
            ease: 'Linear.easeNone'
          }) 
        }
      },

      darkBg(){
        const lastSection = this.home.sections[this.home.sections.length-2]  
        if(lastSection){
          const colors = lastSection.bgColors 
          this.updateBgColor({ startColor : colors.startColor, stopColor : colors.stopColor })
        }
      },


      // Accessibility :: reduced_motion
      // only render canvas upon state changes rather than all the time
      deferRender(){
        setTimeout(()=>{
          if(this.reduced_motion){
            this.onTick(performance.now() / 1000)
          }
        }, 50)
      },



      // HANDLERS ----------------------------------------------------------------

      onPointerMove(event){
        for (var i = 0; i < this.views.length; i++) {
          this.views[i].pointerMove(event)
        }
      },

      onPointerStart(event){
        for (var i = 0; i < this.views.length; i++) {
          this.views[i].pointerStart()
        }
      },

      onPointerEnd(event){
        for (var i = 0; i < this.views.length; i++) {
          this.views[i].pointerEnd()
        }
      },

      onTick (time) {


        const dt = (time - this.prevTime) / 1000 

        if(this.rendering){ 
          if(this.material){
            this.material.uniforms.time.value += dt 
          }

          this.tick(dt, time )
          this.prevTime = time
        }
      },

      onScrollLock(value){
        this.setScrollLock(value)
      },

      onClick(){
        var img = new Image();
        img.onload = ()=>{

          var $a = document.createElement('a');
          $a.download = 'nzta-screenshot.png';
          $a.href = img.src;
          $a.click();

          this.renderer.preserveDrawingBuffer = false
          this.renderer.setClearColor(this.state.bgColor , 1)

        }

        this.renderer.setClearColor(this.state.bgColor , 0)
        this.renderer.preserveDrawingBuffer = true
        this.render()
        img.src = this.renderer.domElement.toDataURL("image/png")

      },

      onBgColorUpdate(){
        this.material.uniforms.startColor.value = new THREE.Color(this.tweenObj.startColor)
        this.material.uniforms.stopColor.value = new THREE.Color(this.tweenObj.stopColor)
      },

      onTintUpdate(){
        this.material.uniforms.lightTintAmt.value = this.tweenObj.lightTintAmt
        this.material.uniforms.darkTintAmt.value = this.tweenObj.darkTintAmt
      },

      onStopRender(){
        this.rendering = false
      },

      onStartRender(){
        this.rendering = true
      },


      // UPDATES ----------------------------------------------------------------

      tick (dt, time) {

        for (var i = 0; i < this.views.length; i++) {
          this.views[i].tick(dt, time)
        }

        this.renderer.setRenderTarget( null )
        this.renderer.render(this.scene, this.camera )
      },

      resize () {

        let ww = window.innerWidth
        let wh = window.innerHeight

        if (this.camera) {
          this.camera.left = (ww / -2)
          this.camera.right = (ww / 2)
          this.camera.top = (wh / 2)
          this.camera.bottom = (wh / -2)
          this.camera.updateProjectionMatrix()
        }

        for (var i = 0; i < this.views.length; i++) {
          this.views[i].resize()
        }

        if(this.plane){
          this.plane.scale.x = ww
          this.plane.scale.y = wh
        }

        this.renderer.setSize(ww, wh)


        // manual update if reduced motion  
        if(this.reduced_motion){
          this.onTick(performance.now() / 1000)
        }
      },

      updateGUI () { },

      updateRoute(){
        switch(this.$route.name) {
          case PATHS.LOADING:
            this.routeLoading()
            break;
          case PATHS.HOME:
            this.routeHome()
            break;
          case PATHS.RATING_ONE:
            this.routeOneStar()
            break;
          case PATHS.RATING_TWO:
            this.routeTwoStar()
            break;
          case PATHS.RATING_THREE:
            this.routeThreeStar()
            break;
          case PATHS.RATING_FOUR:
            this.routeFourStar()
            break;
          case PATHS.RATING_FIVE:
            this.routeFiveStar()
            break;
        }
      },

      updateLoadState(val){
        switch(val) {
          case LOAD.STATES.MAIN_LOADED:
            this.mainLoaded()
            break;
          case LOAD.STATES.BUBBLE_LOADED:
            this.bubbleLoaded()
            break;
          case LOAD.STATES.CHARACTER_LOADED:
            this.characterLoaded()
            break;
          case LOAD.STATES.COMPLETE:
            this.loadComplete()
            break;
        }
      },

      updateModalState(val){
        switch(val) {
          case MODAL_STATES.MENU:
            this.modalMenu()
            break;
          case MODAL_STATES.NONE:
            this.modalNone()
            break;
        }
      },

      getDirection(val, prev){
        let dir
        if(prev) {
          dir = val.index < prev.index ? -1 : 1
        } else {
          dir = 0
        }
        return dir
      },

      updateHomeState(val, prev){

        const dir = this.getDirection(val, prev)
        switch(val.id) {
          case HOME.SECTIONS.WELCOME:
            this.welcome(val, prev, dir)
            break;
          case HOME.SECTIONS.STAR_RATING_FIVE:
            this.ratingFive(val, prev, dir)
            break;
          case HOME.SECTIONS.STAR_RATING_FOUR:
            this.ratingFour(val, prev, dir)
            break;
          case HOME.SECTIONS.STAR_RATING_THREE:
            this.ratingThree(val, prev, dir)
            break;
          case HOME.SECTIONS.STAR_RATING_TWO:
            this.ratingTwo(val, prev, dir)
            break;
          case HOME.SECTIONS.STAR_RATING_ONE:
            this.ratingOne(val, prev, dir)
            break;
          case HOME.SECTIONS.CTA:
            this.cta(val, prev, dir)
            break;
        }
      },


      // modal

      modalNone(){
        this.views.forEach( view => view.modalNone() ) 
      },

      modalMenu(){
        this.views.forEach( view => view.modalMenu() ) 
      },


      // routes

      routeLoading(){
        this.views.forEach( view => view.routeLoading() )
      },

      routeHome(){
        this.views.forEach( view => view.routeHome() )
        this.hideOverlay()

        if(this.home.current_section){
          this.updateBgColor()
        }
      },

      routeOneStar(){
        this.showOverlay()
        this.views.forEach( view => view.routeOneStar() )
        this.darkBg()
      },

      routeTwoStar(){
        this.showOverlay()
        this.views.forEach( view => view.routeTwoStar() )
        this.darkBg()
      },

      routeThreeStar(){
        this.showOverlay()
        this.views.forEach( view => view.routeThreeStar() )
        this.darkBg()
      },

      routeFourStar(){
        this.showOverlay()
        this.views.forEach( view => view.routeFourStar() )
        this.darkBg()
      },

      routeFiveStar(){
        this.showOverlay()
        this.views.forEach( view => view.routeFiveStar() )
        this.darkBg()
      },


      // home state
      welcome(val, prev, dir){
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.welcome(val, prev, dir) )
        this.updateTint(this.welcomeDarkTint, this.welcomeLightTint)
      },

      ratingFive(val, prev, dir){
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.ratingFive(val, prev, dir) )
        this.updateBgColor()
        this.updateTint(0, 0)
      },

      ratingFour(val, prev, dir){
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.ratingFour(val, prev, dir) )
        this.updateBgColor()
        this.updateTint(0, 0)
      },

      ratingThree(val, prev, dir){
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.ratingThree(val, prev, dir) )
        this.updateBgColor()
        this.updateTint(0, 0)
      },

      ratingTwo(val, prev, dir){
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.ratingTwo(val, prev, dir) )
        this.updateBgColor()
        this.updateTint(0, 0)
      },

      ratingOne(val, prev, dir){ 
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.ratingOne(val, prev, dir) )
        this.updateBgColor()
        this.updateTint(0, 0)
      },

      cta(val, prev, dir){ 
        gsap.killTweensOf(this.tweenObj )
        this.views.forEach( v => v.cta(val, prev, dir) ) 
        this.updateTint(0, 0)
      },


      // load states
      mainLoaded(){

        this.createTextures()

        this.setupViews()
        this.addEvents()

        this.views.forEach( v => v.mainLoaded() ) 
      },

      bubbleLoaded(){ 
        this.views.forEach( v => v.bubbleLoaded() )  
        gsap.to(this.$refs.canvas, 2 * this.motion_clamp, {
          opacity: 1,
          ease: 'sine.inOut'
        })
      },
      characterLoaded(){ this.views.forEach( v => v.characterLoaded() ) },
      loadComplete(){ this.views.forEach( v => v.loadComplete() ) }

    },

    watch: {

      'reduced_motion'(val){

        this.views.forEach( view => view.setReducedMotion( val ) )

        if(val){
          this.removeRenderEvent()
        } else {
          this.addRenderEvent()
        }
      },

      '$route'(val){
        this.updateRoute(val)
        this.deferRender()
      },

      'home.current_section'(val, prev){
        this.updateHomeState(val, prev)
        this.deferRender()
      },

      'loading.state'(val){
        this.updateLoadState(val)
        this.deferRender()
      },

      'modal_state'(val){
        this.updateModalState(val)
        this.deferRender()
      } 
    },

  }
</script>

<style lang="sass">

  @import "src/styles/common.sass"

  #stage

    +full()
    +flex-center()

    canvas
      opacity: 0

    .btn
      cursor: pointer
      padding: 5px
      background: red
      position: absolute
      top: 10px
      right : 10px


    .gui-wrap
      position: absolute
      top: 0
      left: 10%
      z-index: 100 



</style>
